import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Alert,
  Snackbar,
  Stack,
  Avatar,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Person,
  Palette,
  AccessTime,
  Save,
  Cancel
} from '@mui/icons-material';
import { useUserPreferences, PREFERENCE_KEYS, DEFAULT_PREFERENCES } from '../hooks/useUserPreferences';
import { getTimezoneDisplayName } from '../utils/dateUtils';
import { useParams } from 'react-router-dom';
import ContactManager from '../components/ContactManager';
import UserWorkingCalendarPanel from '../components/UserWorkingCalendarPanel';
import api from '../services/api';
import {
  normalizePersonalWorkingHoursSchedule,
  type PersonalWorkingHoursSchedule,
  type DayOfWeekKey
} from '../utils/personalWorkingHours';

function truncateForTabLabel(name: string, maxLen = 16): string {
  const t = name.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

interface UserPreferencesProps {
  user: any;
}

const WORK_CALENDAR_TAB = 2;

const UserPreferences: React.FC<UserPreferencesProps> = ({ user }) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const {
    preferences,
    loading,
    error,
    getPreference,
    setPreference
  } = useUserPreferences();

  const [tab, setTab] = useState(0);
  const [orgName, setOrgName] = useState<string>('');
  const [orgNameLoading, setOrgNameLoading] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // Local state for clock24h to ensure immediate UI update
  const [clock24h, setClock24h] = useState<boolean>(() => {
    const v = getPreference(PREFERENCE_KEYS.CLOCK_24H, DEFAULT_PREFERENCES[PREFERENCE_KEYS.CLOCK_24H]);
    if (typeof v === 'boolean') return v;
    return v === 'true' || v === true;
  });

  // Update local state when preferences change
  useEffect(() => {
    const currentValue = getPreference(PREFERENCE_KEYS.CLOCK_24H, DEFAULT_PREFERENCES[PREFERENCE_KEYS.CLOCK_24H]);
    if (typeof currentValue === 'boolean') {
      setClock24h(currentValue);
    } else {
      setClock24h(currentValue === 'true' || currentValue === true);
    }
  }, [preferences, getPreference]);

  const workingHoursKey = PREFERENCE_KEYS.TIMESHEET_DEFAULT_WORKING_HOURS;
  const cloneWorkingHours = (v: unknown): PersonalWorkingHoursSchedule =>
    normalizePersonalWorkingHoursSchedule(v);

  const [workingHoursDraft, setWorkingHoursDraft] = useState<PersonalWorkingHoursSchedule>(() => {
    return cloneWorkingHours(getPreference(workingHoursKey, DEFAULT_PREFERENCES[workingHoursKey]));
  });

  useEffect(() => {
    setWorkingHoursDraft(
      cloneWorkingHours(getPreference(workingHoursKey, DEFAULT_PREFERENCES[workingHoursKey]))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);

  const DAYS: Array<{ key: DayOfWeekKey; label: string }> = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  const handleCancelWorkingHours = () => {
    setWorkingHoursDraft(
      cloneWorkingHours(getPreference(workingHoursKey, DEFAULT_PREFERENCES[workingHoursKey]))
    );
  };

  const handleSaveWorkingHours = async () => {
    await handlePreferenceChange(workingHoursKey, JSON.stringify(workingHoursDraft), 'json');
  };

  useEffect(() => {
    if (!organizationId && tab === WORK_CALENDAR_TAB) {
      setTab(0);
    }
  }, [organizationId, tab]);

  useEffect(() => {
    if (!organizationId) {
      setOrgName('');
      setOrgNameLoading(false);
      return;
    }

    let cancelled = false;
    setOrgNameLoading(true);
    (async () => {
      try {
        const res = await api.getOrganization(organizationId);
        const data = res?.data ?? res;
        if (!cancelled) {
          setOrgName(typeof data?.name === 'string' ? data.name : 'Organization');
        }
      } catch {
        if (!cancelled) {
          setOrgName('Organization');
        }
      } finally {
        if (!cancelled) {
          setOrgNameLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const workCalendarTabLabel = organizationId
    ? orgNameLoading
      ? 'Work calendar (…)'
      : `Work calendar (${truncateForTabLabel(orgName || 'Organization')})`
    : null;

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: user?.displayName || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handlePreferenceChange = async (key: string, value: any, type: string = 'string') => {
    try {
      await setPreference(key, value, type);
      setSnackbar({
        open: true,
        message: 'Preference updated successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to update preference',
        severity: 'error'
      });
    }
  };

  const handleProfileSave = () => {
    // TODO: Implement profile update logic
    setSnackbar({
      open: true,
      message: 'Profile updated successfully',
      severity: 'success'
    });
  };

  const handlePasswordChange = () => {
    // TODO: Implement password change logic
    if (profileForm.newPassword !== profileForm.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }
    setSnackbar({
      open: true,
      message: 'Password changed successfully',
      severity: 'success'
    });
    // Reset password fields
    setProfileForm(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Loading preferences...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const contentMaxWidth = tab === WORK_CALENDAR_TAB ? undefined : { xs: 600, lg: 900 };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person />
        Profile & Preferences
      </Typography>

      <Tabs
        value={organizationId ? tab : Math.min(tab, 1)}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="General" />
        <Tab label="Working hours" />
        {organizationId && user?.id && (
          <Tab
            label={
              <Tooltip title={orgName ? `Work calendar — ${orgName}` : 'Work calendar'} placement="top">
                <span>{workCalendarTabLabel ?? 'Work calendar'}</span>
              </Tooltip>
            }
          />
        )}
      </Tabs>

      <Stack spacing={3} sx={{ maxWidth: contentMaxWidth }}>
        {(organizationId ? tab : Math.min(tab, 1)) === 0 && (
          <>
        <Typography variant="body2" color="text.secondary">
          Applies to your account in all organizations.
        </Typography>

        {/* Profile Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              Profile Information
            </Typography>
            
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 64, height: 64, fontSize: '1.5rem' }}>
                  {profileForm.displayName.charAt(0) || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6">{profileForm.displayName || 'User'}</Typography>
                  <Typography variant="body2" color="text.secondary">Manage your contact information below</Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Display Name"
                value={profileForm.displayName}
                onChange={(e) => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
              />

              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleProfileSave}
              >
                Save Profile
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            
            <Stack spacing={2}>
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={profileForm.currentPassword}
                onChange={(e) => setProfileForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              />

              <TextField
                fullWidth
                type="password"
                label="New Password"
                value={profileForm.newPassword}
                onChange={(e) => setProfileForm(prev => ({ ...prev, newPassword: e.target.value }))}
              />

              <TextField
                fullWidth
                type="password"
                label="Confirm New Password"
                value={profileForm.confirmPassword}
                onChange={(e) => setProfileForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                error={profileForm.newPassword !== '' && profileForm.newPassword !== profileForm.confirmPassword}
                helperText={profileForm.newPassword !== '' && profileForm.newPassword !== profileForm.confirmPassword ? 'Passwords do not match' : ''}
              />

              <Button
                variant="contained"
                onClick={handlePasswordChange}
                disabled={!profileForm.currentPassword || !profileForm.newPassword || !profileForm.confirmPassword}
              >
                Change Password
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Contact Management */}
        <Card>
          <CardContent>
            <ContactManager 
              userId={user?.id} 
              onContactUpdate={() => {
                // Refresh user data or handle contact updates
                setSnackbar({
                  open: true,
                  message: 'Contact information updated',
                  severity: 'success'
                });
              }}
            />
          </CardContent>
        </Card>

        {/* Display Preferences */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Palette />
              Display Preferences
            </Typography>
            
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={getPreference(PREFERENCE_KEYS.THEME, DEFAULT_PREFERENCES[PREFERENCE_KEYS.THEME])}
                  onChange={(e) => handlePreferenceChange(PREFERENCE_KEYS.THEME, e.target.value)}
                  label="Theme"
                >
                  <MenuItem value="light">Light Mode</MenuItem>
                  <MenuItem value="dark">Dark Mode</MenuItem>
                  <MenuItem value="auto">Auto (System)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Date Format</InputLabel>
                <Select
                  value={getPreference(PREFERENCE_KEYS.DATE_FORMAT, DEFAULT_PREFERENCES[PREFERENCE_KEYS.DATE_FORMAT])}
                  onChange={(e) => handlePreferenceChange(PREFERENCE_KEYS.DATE_FORMAT, e.target.value)}
                  label="Date Format"
                >
                  <MenuItem value="MMM dd, yyyy">Jan 15, 2024</MenuItem>
                  <MenuItem value="MM/dd/yyyy">01/15/2024</MenuItem>
                  <MenuItem value="dd/MM/yyyy">15/01/2024</MenuItem>
                  <MenuItem value="yyyy-MM-dd">2024-01-15</MenuItem>
                  <MenuItem value="dd MMM yyyy">15 Jan 2024</MenuItem>
                  <MenuItem value="MMMM dd, yyyy">January 15, 2024</MenuItem>
                  <MenuItem value="dd/MM/yy">15/01/24</MenuItem>
                  <MenuItem value="MM/dd/yy">01/15/24</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={getPreference(PREFERENCE_KEYS.TIMEZONE, DEFAULT_PREFERENCES[PREFERENCE_KEYS.TIMEZONE])}
                  onChange={(e) => handlePreferenceChange(PREFERENCE_KEYS.TIMEZONE, e.target.value)}
                  label="Timezone"
                >
                  <MenuItem value="UTC">{getTimezoneDisplayName('UTC')}</MenuItem>
                  <MenuItem value="America/New_York">{getTimezoneDisplayName('America/New_York')}</MenuItem>
                  <MenuItem value="America/Chicago">{getTimezoneDisplayName('America/Chicago')}</MenuItem>
                  <MenuItem value="America/Denver">{getTimezoneDisplayName('America/Denver')}</MenuItem>
                  <MenuItem value="America/Los_Angeles">{getTimezoneDisplayName('America/Los_Angeles')}</MenuItem>
                  <MenuItem value="America/Anchorage">{getTimezoneDisplayName('America/Anchorage')}</MenuItem>
                  <MenuItem value="Pacific/Honolulu">{getTimezoneDisplayName('Pacific/Honolulu')}</MenuItem>
                  <MenuItem value="Europe/London">{getTimezoneDisplayName('Europe/London')}</MenuItem>
                  <MenuItem value="Europe/Paris">{getTimezoneDisplayName('Europe/Paris')}</MenuItem>
                  <MenuItem value="Europe/Berlin">{getTimezoneDisplayName('Europe/Berlin')}</MenuItem>
                  <MenuItem value="Europe/Rome">{getTimezoneDisplayName('Europe/Rome')}</MenuItem>
                  <MenuItem value="Europe/Madrid">{getTimezoneDisplayName('Europe/Madrid')}</MenuItem>
                  <MenuItem value="Europe/Amsterdam">{getTimezoneDisplayName('Europe/Amsterdam')}</MenuItem>
                  <MenuItem value="Europe/Stockholm">{getTimezoneDisplayName('Europe/Stockholm')}</MenuItem>
                  <MenuItem value="Europe/Oslo">{getTimezoneDisplayName('Europe/Oslo')}</MenuItem>
                  <MenuItem value="Europe/Copenhagen">{getTimezoneDisplayName('Europe/Copenhagen')}</MenuItem>
                  <MenuItem value="Europe/Helsinki">{getTimezoneDisplayName('Europe/Helsinki')}</MenuItem>
                  <MenuItem value="Europe/Warsaw">{getTimezoneDisplayName('Europe/Warsaw')}</MenuItem>
                  <MenuItem value="Europe/Moscow">{getTimezoneDisplayName('Europe/Moscow')}</MenuItem>
                  <MenuItem value="Asia/Tokyo">{getTimezoneDisplayName('Asia/Tokyo')}</MenuItem>
                  <MenuItem value="Asia/Seoul">{getTimezoneDisplayName('Asia/Seoul')}</MenuItem>
                  <MenuItem value="Asia/Shanghai">{getTimezoneDisplayName('Asia/Shanghai')}</MenuItem>
                  <MenuItem value="Asia/Hong_Kong">{getTimezoneDisplayName('Asia/Hong_Kong')}</MenuItem>
                  <MenuItem value="Asia/Singapore">{getTimezoneDisplayName('Asia/Singapore')}</MenuItem>
                  <MenuItem value="Asia/Kolkata">{getTimezoneDisplayName('Asia/Kolkata')}</MenuItem>
                  <MenuItem value="Asia/Dubai">{getTimezoneDisplayName('Asia/Dubai')}</MenuItem>
                  <MenuItem value="Australia/Sydney">{getTimezoneDisplayName('Australia/Sydney')}</MenuItem>
                  <MenuItem value="Australia/Melbourne">{getTimezoneDisplayName('Australia/Melbourne')}</MenuItem>
                  <MenuItem value="Pacific/Auckland">{getTimezoneDisplayName('Pacific/Auckland')}</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={clock24h}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      // Update local state immediately for responsive UI
                      setClock24h(newValue);
                      // Save to backend
                      await handlePreferenceChange(PREFERENCE_KEYS.CLOCK_24H, newValue, 'boolean');
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime />
                    <Typography>24-Hour Clock</Typography>
                  </Box>
                }
              />
            </Stack>
          </CardContent>
        </Card>
          </>
        )}

        {(organizationId ? tab : Math.min(tab, 1)) === 1 && (
          <>
        <Typography variant="body2" color="text.secondary">
          Default timesheet layout for your account in all organizations. This is not your organization&apos;s official
          working calendar.
        </Typography>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime />
              Default working hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Planned hours per weekday for your personal dashboard and timesheet defaults.
            </Typography>

            <Stack spacing={2}>
              {DAYS.map((day) => {
                const cfg = workingHoursDraft[day.key] || { enabled: false, hours: 0 };
                const enabled = cfg.enabled;

                return (
                  <Box
                    key={day.key}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 2, lg: 1.5 },
                      flexWrap: { xs: 'wrap', lg: 'nowrap' }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setWorkingHoursDraft((prev) => ({
                              ...prev,
                              [day.key]: {
                                enabled: checked,
                                hours: checked ? prev[day.key]?.hours || 8 : 0
                              }
                            }));
                          }}
                          disabled={loading}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {day.label}
                        </Typography>
                      }
                    />

                    <TextField
                      size="small"
                      type="number"
                      label="Hours"
                      value={enabled ? cfg.hours : 0}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const hours = raw === '' ? 0 : parseFloat(raw);
                        setWorkingHoursDraft((prev) => ({
                          ...prev,
                          [day.key]: { enabled: true, hours }
                        }));
                      }}
                      inputProps={{ step: 0.25, min: 0 }}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading || !enabled}
                      sx={{ width: 120 }}
                    />
                  </Box>
                );
              })}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelWorkingHours} disabled={loading}>
                  Cancel
                </Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSaveWorkingHours} disabled={loading}>
                  Save working hours
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
          </>
        )}

        {organizationId && user?.id && tab === WORK_CALENDAR_TAB && (
          <>
            <Typography variant="body2" color="text.secondary">
              Only for <strong>{orgName || 'this organization'}</strong>. Your manager sets calendar assignment and
              employment %. Use <strong>Edit my work calendar</strong> for your adjustments on top.
            </Typography>
            <UserWorkingCalendarPanel
              key={organizationId}
              organizationId={organizationId}
              userId={user.id}
              allowOrgAdminEdits={false}
            />
          </>
        )}

      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserPreferences;

